import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';

export default function ManageAlertsRemindersPage() {
  const [audit, setAudit] = React.useState({ rows: [], total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const loadAudit = React.useCallback(async () => {
    setAuditLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/alerts-reminders/audit', { limit: 100, offset: 0 });
      setAudit({ rows: res?.rows || [], total: res?.total ?? 0 });
    } catch (e) {
      setError(e?.message || 'Failed to load audit log.');
    } finally {
      setAuditLoading(false);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  if (loading && !audit.rows.length && !error) {
    return (
      <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Alerts and reminders</Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only log of automated reminder emails sent by the server (e.g. important-occasion reminders). Devotees maintain occasions on the devotee profile; email wording is configured under Email templates.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => loadAudit()} disabled={auditLoading}>
          {auditLoading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Audit log</Typography>
          {auditLoading && !audit.rows.length ? (
            <CircularProgress size={24} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sent / created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Devotee</TableCell>
                  <TableCell>Occasion id</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Subject</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(audit.rows || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">No reminder emails logged yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {(audit.rows || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.sent_at ? String(r.sent_at).slice(0, 19) : String(r.created_at || '').slice(0, 19)}</TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell>{r.calendar_year}</TableCell>
                    <TableCell>{r.devotee_id}</TableCell>
                    <TableCell>{r.occasion_id}</TableCell>
                    <TableCell>{r.recipient_email}</TableCell>
                    <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject_snapshot}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Total rows (this reminder kind): {audit.total}
          </Typography>
        </CardContent>
      </Card>
    </PageLayout>
  );
}
